import { useState } from 'react'
import { createSnapshot } from './snapshotsApi'
import PeriodPicker from '../reports/PeriodPicker'
import type { PeriodValue } from '../reports/schemas'
import type { Snapshot } from '../../types/snapshot'

type Props = {
  clientId: number
  reportId: number
  defaultPeriod: PeriodValue
  defaultName?: string
  onClose: () => void
  onCreated: (snapshot: Snapshot) => void
}

function defaultSnapshotName(): string {
  return `Snapshot du ${new Date().toLocaleDateString('fr-FR')}`
}

export function CreateSnapshotModal({ clientId, reportId, defaultPeriod, defaultName, onClose, onCreated }: Props) {
  const [name, setName] = useState(defaultName ?? defaultSnapshotName())
  const [period, setPeriod] = useState<PeriodValue>(defaultPeriod)
  const [accessMode, setAccessMode] = useState<'private' | 'public_link'>('private')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit() {
    setSaving(true)
    setError(null)
    try {
      const snapshot = await createSnapshot(clientId, reportId, { name, period, accessMode })
      onCreated(snapshot)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur inconnue')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(27, 37, 89, 0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: 12,
          padding: 24,
          width: 420,
          maxWidth: '90vw',
          boxShadow: '0 20px 60px rgba(27, 37, 89, 0.25)',
        }}
      >
        <h2 style={{ color: '#1B2559', fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          Générer un snapshot
        </h2>

        <label htmlFor="snapshot-name" style={{ display: 'block', color: '#1B2559', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>
          Nom
        </label>
        <input
          id="snapshot-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{
            width: '100%',
            border: '1px solid #E2E4ED',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 14,
            color: '#1B2559',
            marginBottom: 16,
          }}
        />

        <PeriodPicker value={period} onChange={setPeriod} />

        <fieldset style={{ border: 'none', padding: 0, margin: '16px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <legend style={{ color: '#8B8FA7', fontSize: 12, padding: 0, marginBottom: 4 }}>Accès</legend>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#1B2559' }}>
            <input
              type="radio"
              name="accessMode"
              checked={accessMode === 'private'}
              onChange={() => setAccessMode('private')}
            />
            Privé (membres du projet)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#1B2559' }}>
            <input
              type="radio"
              name="accessMode"
              checked={accessMode === 'public_link'}
              onChange={() => setAccessMode('public_link')}
            />
            Lien public (partageable sans login)
          </label>
          {accessMode === 'public_link' && (
            <p style={{ color: '#B7791F', fontSize: 12, background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', margin: 0 }}>
              Toute personne disposant du lien pourra voir ce rapport et ses données, sans connexion. Supprime le snapshot pour révoquer le lien.
            </p>
          )}
        </fieldset>

        {error && (
          <p style={{ color: '#E53E3E', fontSize: 13, marginBottom: 12 }}>{error}</p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              background: 'transparent',
              border: '1px solid #E2E4ED',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              color: '#1B2559',
              cursor: 'pointer',
            }}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            style={{
              background: '#6366F1',
              border: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontSize: 14,
              fontWeight: 500,
              color: '#FFFFFF',
              cursor: 'pointer',
            }}
          >
            {saving ? 'Génération...' : 'Générer'}
          </button>
        </div>
      </div>
    </div>
  )
}
